import { useCallback, useState } from "react";

import { strings } from "@/constants";

export interface UseActionReturnInterface<TArgs extends unknown[], TResult> {
    data: TResult | null,
    error: string | null,
    isLoading: boolean,
    onReset: () => void,
    onRun: (...args: TArgs) => Promise<TResult>,
}

export const useAction = <TArgs extends unknown[], TResult>(actionHandler: (...args: TArgs) => Promise<TResult>): UseActionReturnInterface<TArgs, TResult> => {
    const [data, setData] = useState<TResult | null>(null);

    const [error, setError] = useState<string | null>(null);

    const [isLoading, setIsLoading] = useState(false);

    const runHandler = useCallback(
        async (...args: TArgs) => {
            setIsLoading(true);

            setError(null);

            try {
                const result = await actionHandler(...args);

                setData(result);

                return result;
            } catch (err) {
                const message = err instanceof Error ? err.message : strings.errors.tryAgain;

                setError(message);

                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [actionHandler],
    );

    const resetHandler = useCallback(
        () => {
            setData(null);

            setError(null);
        },
        [],
    );

    return {
        data,
        error,
        isLoading,
        onReset: resetHandler,
        onRun: runHandler,
    };
};
